import { Box } from '@mui/material';
import { DataGridPro, useGridApiRef } from '@mui/x-data-grid-pro';
import {
  getStatusColumnObject,
  paintStatusWithMore,
  processRowUpdateForStatusOnly,
  R12_FORECAST_COLUMN_WIDTH,
  R12_SPEND_COLUMN_WIDTH,
  SPEND_COLUMNS,
  SUPPLIER_NAME_COLUMN_WIDTH,
  SUPPLIER_NUMBER_COLUMN_WIDTH,
  SUPPLIER_STATUS_COLUMN_WIDTH,
  StatusBadge
} from './SupplierDataGridUtils';
import { useEffect, useMemo } from 'react';
import { STRATEGIC } from '../../shared/AppConstants';
import { supplierDataGridSx } from './supplierDataGridStyles';

const getTreeDataPath = (row) => row.hierarchy;
const CHILD_COLUMN_WIDTHS = {
  grouping: SUPPLIER_NUMBER_COLUMN_WIDTH + SUPPLIER_NAME_COLUMN_WIDTH,
  status: SUPPLIER_STATUS_COLUMN_WIDTH,
  r12Spend: R12_SPEND_COLUMN_WIDTH,
  r12Forecast: R12_FORECAST_COLUMN_WIDTH
};

function getStatusColumnForPanel(isEdit) {
  return {
    ...getStatusColumnObject(isEdit, STRATEGIC),
    renderCell: (params) => {
      const row = params.row;
      const isStatusExcption =
        !row.notFullySelected &&
        row.parentStatus &&
        row.parentStatus !== row.status;
      const updatedVal = isStatusExcption ? (
        <StatusBadge statusMsg={params.value} />
      ) : (
        params.value
      );

      return paintStatusWithMore(
        row.statusMapSize,
        updatedVal,
        row.notFullySelected,
        isEdit
      );
    }
  };
}

export function SupplierDataGridTreeData({
  isEdit,
  row,
  updateSupplierStatus,
  refresh,
  findExpandedIds,
  setExpandedRowInfo,
  handleCellClick,
  handleProcessRowUpdateError
}) {
  const childApiRef = useGridApiRef();

  const detailColumns = useMemo(() => {
    const statusColumn = {
      ...getStatusColumnForPanel(isEdit),
      width: CHILD_COLUMN_WIDTHS.status,
      minWidth: CHILD_COLUMN_WIDTHS.status,
      flex: 0
    };

    const spendColumns = SPEND_COLUMNS.map((column) => {
      if (column.field === 'r12Spend') {
        return {
          ...column,
          width: CHILD_COLUMN_WIDTHS.r12Spend,
          minWidth: CHILD_COLUMN_WIDTHS.r12Spend,
          flex: 0
        };
      }

      if (column.field === 'r12Forecast') {
        return {
          ...column,
          width: CHILD_COLUMN_WIDTHS.r12Forecast,
          minWidth: CHILD_COLUMN_WIDTHS.r12Forecast,
          flex: 0
        };
      }

      return column;
    });

    return [statusColumn, ...spendColumns];
  }, [isEdit]);

  const handleEvent = (
    params // Set row expansion state
  ) => setExpandedRowInfo(row.id, params.id, params.childrenExpanded);

  useEffect(() => {
    if (childApiRef && row?.unitGroupsSpend.length > 0) {
      childApiRef.current.subscribeEvent('rowExpansionChange', handleEvent);

      let expandedIds = findExpandedIds(row.id);

      if (expandedIds && expandedIds.length > 0) {
        // Expand the rows
        expandedIds.forEach(
          (id) =>
            id <= row.unitGroupsSpend.length &&
            childApiRef.current.setRowChildrenExpansion(id, true)
        );
      }
    }
  }, [childApiRef, row]);

  return (
    <Box sx={{ ml: (theme) => theme.spacing(12.75) }}>
      {row.unitGroupsSpend.length > 0 && (
        <DataGridPro
          key={`ScopeGrid${refresh}`}
          apiRef={childApiRef}
          isCellEditable={(params) => !params.row?.notFullySelected}
          onCellClick={handleCellClick}
          onProcessRowUpdateError={handleProcessRowUpdateError}
          processRowUpdate={(updatedRow) => {
            return processRowUpdateForStatusOnly(
              updatedRow,
              updateSupplierStatus
            );
          }}
          treeData
          getTreeDataPath={getTreeDataPath}
          groupingColDef={{
            headerName: 'Unit Group',
            width: CHILD_COLUMN_WIDTHS.grouping
          }}
          density="compact"
          columns={detailColumns}
          rows={row.unitGroupsSpend}
          sx={supplierDataGridSx}
          hideFooter
        />
      )}
    </Box>
  );
}
